"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createSafeContextWithHoC = exports.createSafeContext = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const uninitializedValue = Symbol('Uninitialized context value');
/**
 * Create a tuple containing a React context provider and a hook to access its value
 * @param identifier a camel case identifier which will be used for the context `displayName`
 * @param initialValue a non null value which will be passed to `React.createContext` if present
 * @returns A tuple containing a React context provider and a value accessor hook
 * @throws The value accessor hook will throw if no `initialValue` was provided and there is no context provider in its parents
 * @example const [ValueProvider, useValue] = createSafeContext<string>('value');
 *          const [ValueProvider, useValue] = createSafeContext('value', 'default');
 * @description For these 2 examples, `ValueProvider.displayName === 'ValueContext.Provider'`
 */
function createSafeContext(identifier, initialValue) {
    const pascalCasedIdentifier = identifier.charAt(0).toUpperCase() + identifier.slice(1);
    const GenericContext = (0, react_1.createContext)(initialValue !== null && initialValue !== void 0 ? initialValue : uninitializedValue);
    GenericContext.displayName = `${pascalCasedIdentifier}Context`;
    function useGenericContext() {
        const value = (0, react_1.useContext)(GenericContext);
        if (value === uninitializedValue) {
            throw new Error(`use${pascalCasedIdentifier} needs a ${pascalCasedIdentifier}Provider in its ancestors`);
        }
        return value;
    }
    return [GenericContext.Provider, useGenericContext];
}
exports.createSafeContext = createSafeContext;
/**
 * @see {@link createSafeContext} for parameters explanation
 * @returns A tuple containing a React context provider, a value accessor hook and a higher-order component which will pass the context value as a prop to the wrapped component
 * @deprecated Use {@link createSafeContext} when you don't need a higher-order component for this context
 */
function createSafeContextWithHoC(key, initialValue) {
    const [SafeContextProvider, useSafeContextValue] = createSafeContext(key, initialValue);
    const withValue = makeWithContextValue(useSafeContextValue, key);
    return [SafeContextProvider, useSafeContextValue, withValue];
}
exports.createSafeContextWithHoC = createSafeContextWithHoC;
function makeWithContextValue(contextValueGetter, key) {
    const pascalCasedKey = key.charAt(0).toUpperCase() + key.slice(1);
    const withDisplayName = `With${pascalCasedKey}`;
    return function withValue(Component) {
        const WithValue = (props) => {
            const value = contextValueGetter();
            const genericProp = { [key]: value };
            return (0, jsx_runtime_1.jsx)(Component, Object.assign({}, props, genericProp));
        };
        WithValue.displayName = withDisplayName;
        return WithValue;
    };
}
//# sourceMappingURL=context.js.map